// Procedural chiptune SFX via the Web Audio API — zero asset files. The
// "correct" arpeggio pitches UP with the streak, so a hot streak literally
// sounds more triumphant.

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;

function ac(): AudioContext {
  if (!ctx) {
    ctx = new AudioContext();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : 0.5;
    master.connect(ctx.destination);
  }
  return ctx;
}

/** Browsers block audio until a user gesture — call this on first interaction. */
export function unlockAudio() {
  const c = ac();
  if (c.state === "suspended") void c.resume();
}

export function setMuted(m: boolean) {
  muted = m;
  if (master) master.gain.value = m ? 0 : 0.5;
}

export function isMuted() {
  return muted;
}

function tone(opts: {
  freq: number;
  dur: number;
  type?: OscillatorType;
  gain?: number;
  when?: number;
  sweepTo?: number;
}) {
  if (muted) return;
  const c = ac();
  const t0 = c.currentTime + (opts.when ?? 0);
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = opts.type ?? "sine";
  osc.frequency.setValueAtTime(opts.freq, t0);
  if (opts.sweepTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.sweepTo), t0 + opts.dur);
  const peak = opts.gain ?? 0.2;
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.dur);
  osc.connect(gain).connect(master ?? c.destination);
  osc.start(t0);
  osc.stop(t0 + opts.dur + 0.03);
}

export const Sfx = {
  hover() {
    tone({ freq: 880, dur: 0.03, type: "sine", gain: 0.04 });
  },
  pickup() {
    tone({ freq: 440, dur: 0.09, type: "triangle", gain: 0.12 });
  },
  place() {
    tone({ freq: 280, dur: 0.07, type: "sine", gain: 0.1 });
  },
  deal() {
    tone({ freq: 720, dur: 0.05, type: "sine", gain: 0.06 });
  },
  correct(streak: number) {
    const base = 523.25; // C5
    const semis = [0, 4, 7, 12]; // major chord, climbs with streak
    const lift = Math.min(streak, 10);
    semis.forEach((s, i) =>
      tone({ freq: base * Math.pow(2, (s + lift) / 12), dur: 0.2, type: "triangle", gain: 0.15, when: i * 0.055 }),
    );
    tone({ freq: 1760, dur: 0.18, type: "sine", gain: 0.05, when: 0.2 }); // sparkle
  },
  wrong() {
    tone({ freq: 200, dur: 0.22, type: "sawtooth", gain: 0.11, sweepTo: 110 });
  },
  start() {
    const base = 392; // G4 — a rising fanfare to launch the game
    [0, 7, 12, 16].forEach((s, i) =>
      tone({ freq: base * Math.pow(2, s / 12), dur: 0.28, type: "triangle", gain: 0.16, when: i * 0.06 }),
    );
    tone({ freq: 1568, dur: 0.32, type: "sine", gain: 0.06, when: 0.26 }); // shimmer on top
  },
  toggle(on: boolean) {
    tone({ freq: on ? 620 : 380, dur: 0.07, type: "triangle", gain: 0.1, sweepTo: on ? 880 : 300 });
  },
};
